import { useEffect, useState } from "react";
import { X, Download, Loader2, ExternalLink, ZoomIn } from "lucide-react";
import { api } from "../lib/api";

/**
 * Inline PDF (or any browser-renderable file) previewer.
 *
 * Two invocation modes:
 *   • `docId` only          — fetches a fresh signed URL from the
 *                             agent API (`/documents/{id}/download`).
 *   • `directUrl` provided  — skip the fetch. Used from the client
 *                             portal, whose docs already carry a signed
 *                             URL from `/portal/documents` and whose
 *                             axios client can't hit the agent route
 *                             anyway.
 *
 * The file renders in an <iframe> so browsers use their native PDF
 * viewer (search, page nav, print) without triggering a download. A
 * zoom toolbar in the header re-mounts the iframe with a
 * `#zoom={value}` URL fragment — every major browser's built-in PDF
 * viewer respects this hint, so we get zoom without shipping pdf.js.
 *
 * The Download button opens the same URL in a new tab with the
 * `download` attribute, giving agents the "save-to-disk" escape hatch.
 *
 * Works for blank templates, filled Smart-Fill outputs, and
 * countersigned copies alike.
 */
export default function DocPreviewModal({ docId, name, onClose, directUrl, testIdPrefix = "doc-preview" }) {
  const [url, setUrl] = useState(directUrl || null);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState("page-width");

  useEffect(() => {
    if (directUrl) { setUrl(directUrl); return; }
    let cancelled = false;
    api.get(`/documents/${docId}/download`)
      .then(({ data }) => { if (!cancelled) setUrl(data.url); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load this document"); });
    return () => { cancelled = true; };
  }, [docId, directUrl]);

  // Body scroll-lock while modal is open so the iframe doesn't fight the
  // page scroll on mobile.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Zoom presets that map cleanly to the PDF-viewer hash fragment spec.
  // `page-width` is the friendly default — the doc fills the modal on
  // load; agents can scale up if they need to squint at small type.
  const zoomOptions = [
    { key: "page-width", label: "Fit width" },
    { key: "page-fit",   label: "Fit page" },
    { key: "100",        label: "100%" },
    { key: "125",        label: "125%" },
    { key: "150",        label: "150%" },
    { key: "200",        label: "200%" },
  ];
  // Re-attach the fragment on every render so `key` change forces the
  // iframe to remount when zoom is toggled (browsers ignore fragment-
  // only src updates on an existing iframe).
  const srcWithZoom = url ? `${url}${url.includes("#") ? "&" : "#"}zoom=${zoom}&view=FitH` : "";

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testIdPrefix}-title`}
      data-testid={testIdPrefix}
    >
      <div className="w-full max-w-5xl rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-4 border-b border-slate-100 gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Document preview</div>
            <h3 id={`${testIdPrefix}-title`} className="font-heading text-lg font-bold text-slate-900 truncate">
              {name}
            </h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {url && (
              <>
                <div className="hidden md:flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1" title="Zoom">
                  <ZoomIn className="h-3.5 w-3.5 text-slate-500" />
                  <select
                    value={zoom}
                    onChange={(e) => setZoom(e.target.value)}
                    className="text-xs font-semibold text-slate-700 bg-transparent focus:outline-none pr-1"
                    data-testid={`${testIdPrefix}-zoom`}
                  >
                    {zoomOptions.map(o => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={name || "document.pdf"}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  title="Download this document to your device"
                  data-testid={`${testIdPrefix}-download`}
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-2 py-1.5 text-xs font-semibold"
                  title="Open in a new browser tab"
                  data-testid={`${testIdPrefix}-open-tab`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white"
              style={{ background: "var(--primary-hex)" }}
              data-testid={`${testIdPrefix}-close`}
            >
              <X className="h-3.5 w-3.5" /> Close
            </button>
          </div>
        </div>
        <div className="bg-slate-100" style={{ height: "80vh" }}>
          {error && (
            <div className="p-6 text-sm text-rose-700 bg-rose-50" data-testid={`${testIdPrefix}-error`}>
              {error}
            </div>
          )}
          {!url && !error && (
            <div className="flex items-center justify-center h-full text-sm text-slate-500 gap-2" data-testid={`${testIdPrefix}-loading`}>
              <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
            </div>
          )}
          {url && (
            <iframe
              key={zoom}
              src={srcWithZoom}
              title={name}
              className="w-full h-full"
              data-testid={`${testIdPrefix}-iframe`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
